/**
 * iter-111 — SchemaMatchErrorDialog
 *
 * Surfaced when attaching a CSV/dataset to a dashboard fails the schema-match
 * gate (HTTP 422 from `POST /api/dashboards/{id}/attach-source` or from the
 * upload flow). Shows:
 *
 *   • The mismatch headline
 *   • Diff: expected canonical fields vs incoming columns
 *   • Clickable alternatives: the user's own dashboards bound to a more
 *     suitable model, plus platform prebuilt templates that match.
 *
 * No "pick a model" affordance for non-SA users — they either click an
 * alternative dashboard or close the dialog and pick a different file.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowRight, FileText, Sparkles, CheckCircle2 } from "lucide-react";

export default function SchemaMatchErrorDialog({ open, onClose, payload, onPickAlternative }) {
    const nav = useNavigate();
    const expected = payload?.expected_model;
    const expectedFieldNames = (expected?.fields || []).map((f) => f.name);
    const expectedRequired = (expected?.fields || []).filter((f) => f.required).map((f) => f.name);
    const yourCols = payload?.your_columns || [];
    const yourSet = new Set(yourCols.map((s) => (s || "").toLowerCase()));
    const alts = payload?.alternative_dashboards || { yours: [], templates: [] };
    const bo = payload?.best_overall;

    const goTo = (href) => {
        if (onPickAlternative) onPickAlternative();
        onClose();
        nav(href);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="schema-mismatch-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-foreground">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        This source doesn't fit the dashboard
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-auto space-y-5 px-1 py-2">
                    {/* Headline */}
                    <div className="text-sm text-foreground" data-testid="schema-mismatch-message">
                        {payload?.message || "Couldn't match the source to this dashboard's expected fields."}
                    </div>

                    {/* Expected vs your columns diff */}
                    {expected && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="schema-mismatch-diff">
                            <div className="border border-border rounded-sm p-3 space-y-2">
                                <div className="text-[11px] font-mono uppercase text-muted-foreground flex items-center gap-1.5">
                                    <Sparkles className="h-3 w-3" /> Expected · {expected.name || expected.slug}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {(expected.fields || []).map((f) => {
                                        const present = yourSet.has((f.name || "").toLowerCase());
                                        return (
                                            <Badge key={f.name}
                                                variant="outline"
                                                className={`font-mono text-[10px] gap-1 ${
                                                    present ? "border-[hsl(var(--success))] text-[hsl(var(--success))]"
                                                            : f.required ? "border-amber-500/60 text-amber-600 dark:text-amber-400"
                                                            : ""
                                                }`}
                                                data-testid={`schema-expected-field-${f.name}`}>
                                                {present && <CheckCircle2 className="h-2.5 w-2.5" />}
                                                {f.name}{f.required ? "*" : ""}
                                            </Badge>
                                        );
                                    })}
                                </div>
                                {expectedRequired.length > 0 && (
                                    <div className="text-[10px] font-mono text-muted-foreground">
                                        * required field
                                    </div>
                                )}
                            </div>
                            <div className="border border-border rounded-sm p-3 space-y-2">
                                <div className="text-[11px] font-mono uppercase text-muted-foreground flex items-center gap-1.5">
                                    <FileText className="h-3 w-3" /> Your file has
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {yourCols.length === 0 && (
                                        <span className="text-[11px] text-muted-foreground italic">No columns detected</span>
                                    )}
                                    {yourCols.map((c) => (
                                        <Badge key={c}
                                            variant="outline"
                                            className={`font-mono text-[10px] ${
                                                expectedFieldNames.map((n) => n.toLowerCase()).includes((c || "").toLowerCase())
                                                    ? "border-[hsl(var(--success))] text-[hsl(var(--success))]" : "opacity-70"
                                            }`}
                                            data-testid={`schema-your-col-${c}`}>
                                            {c}
                                        </Badge>
                                    ))}
                                </div>
                                {bo && (
                                    <div className="text-[11px] text-muted-foreground pt-1">
                                        Closest match: <span className="text-foreground font-mono">{bo.slug}</span>
                                        {" "}· {Math.round((bo.confidence || 0) * 100)}% confidence
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Alternative dashboards */}
                    {(alts.yours?.length > 0 || alts.templates?.length > 0) && (
                        <div className="space-y-3" data-testid="schema-mismatch-alternatives">
                            <div className="text-[11px] font-mono uppercase text-muted-foreground">
                                Try one of these instead
                            </div>

                            {alts.yours?.length > 0 && (
                                <div>
                                    <div className="text-xs text-muted-foreground mb-1.5">Your dashboards</div>
                                    <div className="space-y-1.5">
                                        {alts.yours.map((a) => (
                                            <button key={a.id}
                                                onClick={() => goTo(`/dashboards/${a.id}`)}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-sm border border-border hover:border-primary/40 hover:bg-accent/40 transition-colors"
                                                data-testid={`schema-alt-yours-${a.id}`}>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate">{a.name}</div>
                                                    <div className="text-[11px] font-mono text-muted-foreground truncate">
                                                        binds to {a.model_slug}
                                                    </div>
                                                </div>
                                                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {alts.templates?.length > 0 && (
                                <div>
                                    <div className="text-xs text-muted-foreground mb-1.5">Available templates</div>
                                    <div className="space-y-1.5">
                                        {alts.templates.map((a) => (
                                            <button key={a.id}
                                                onClick={() => goTo(`/dashboards?template=${a.id}`)}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-sm border border-border hover:border-primary/40 hover:bg-accent/40 transition-colors"
                                                data-testid={`schema-alt-template-${a.id}`}>
                                                <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate">{a.name}</div>
                                                    <div className="text-[11px] font-mono text-muted-foreground truncate">
                                                        binds to {a.model_slug}
                                                    </div>
                                                </div>
                                                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* No alternatives — friendly fallback */}
                    {(!alts.yours || alts.yours.length === 0) && (!alts.templates || alts.templates.length === 0) && (
                        <div className="text-center text-xs text-muted-foreground p-6 border border-dashed border-border rounded-sm" data-testid="schema-mismatch-no-alts">
                            No matching dashboards or templates yet for this data shape.
                            Try uploading a different file or check the expected fields above.
                        </div>
                    )}
                </div>

                <DialogFooter className="pt-3 border-t border-border">
                    <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="schema-mismatch-close">
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
